import { createClient as createSupabaseClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';

interface SupabaseEnv {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
}

function getMetaEnv(): SupabaseEnv {
  if (typeof import.meta !== 'undefined') {
    return (import.meta as any).env ?? {};
  }
  return {};
}

let supabaseInstance: SupabaseClient | null = null;

/**
 * Create or get the singleton Supabase client for frontend use.
 * Uses ANON key and RLS policies.
 */
export function createClient(): SupabaseClient {
  if (supabaseInstance) {
    return supabaseInstance;
  }

  // Works in both browser (Vite) and React Native (process.env)
  const metaEnv = getMetaEnv();

  const supabaseUrl = metaEnv.VITE_SUPABASE_URL || (typeof process !== 'undefined' && process.env.SUPABASE_URL) || '';

  const supabaseAnonKey = metaEnv.VITE_SUPABASE_ANON_KEY || (typeof process !== 'undefined' && process.env.SUPABASE_ANON_KEY) || '';

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  supabaseInstance = createSupabaseClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });

  return supabaseInstance;
}

/**
 * Get the singleton Supabase client (alias for createClient)
 * @deprecated Use createClient() instead
 */
export function getSupabaseClient(): SupabaseClient {
  return createClient();
}

/**
 * Resolve client for use in shared functions.
 * Returns provided client or creates default client.
 *
 * This allows the same functions to be used in:
 * - Frontend (web/mobile) with RLS
 * - Backend agents with service-role access
 */
export function resolveClient(client?: SupabaseClient): SupabaseClient {
  return client || createClient();
}
